import React from "react";

import navigation from "../images/navigation.png";
import controlsAnnotated from "../images/controlsAnnotated.png";

import navigation_zh from "../images/navigation_zh.png";
import controls_zh from "../images/controls_zh.png";

const enAttributes = [
  [
    "CGP ID",
    "Unique identifier for referencing each power plant unit in the database",
  ],
  [
    "Unit Name",
    "Unique name of power generating unit, with technology and capacity (MW)",
  ],
  ["Plant Name", "Name of the power plant, which may have multiple units"],
  ["Capacity (MW)", "Gross generating capacity of unit (MW)"],
  ["Project Status", "In operation, under construction or under planning"],
  [
    <>
      Year of Commission
      <sup>
        <a href="#note1" id="reference1">
          [*]
        </a>
      </sup>
    </>,
    "Year unit entered or is scheduled for commission",
  ],
  [
    "Year of Commission (M&A: Acquisition Year)",
    "Normally the commission year, but if the investment is an M&A and the commission happened before the acquisition, we use the acquisition year instead. This ensures we capture the year China actually began its presence.",
  ],
  ["Technology", "Primary fuel: Coal, gas, solar, wind, etc."],
  ["State/Province", "State or province where the unit is located"],
  ["Country", "Country where the unit is located"],
  ["Region", "Region where the unit is located"],
  [
    "Estimated Annual CO2 Emissions",
    "Estimated annual CO2 emissions in thousand metric tons (ktons)",
  ],
  ["M&A Year", "The year of acquisition"],
  [
    "Deal Type",
    "M&A, greenfield investment, involves both FDI and DFIs or DFI finance only",
  ],
  ["FDI?", "Involving Chinese foreign direct investment or not"],
  ["Investing Company", "Primary Chinese investor"],
  [
    "Investing Company Ownership %",
    "The share of ownership held by the primary Chinese investor, if known",
  ],
  ["FDI Deal Type", "Greenfield or M&A"],
  ["Involving Chinese DFI?", "Involving Chinese DFIs or not"],
  ["Lender", "Which Chinese DFI provided the loan"],
  ["Borrower", "Primary recipient of the loan"],
  [
    "Funding Year",
    "The year that an equity investment is made in the case of FDI (greenfield and M&A) and the year a loan is committed in the case of DFIs",
  ],
  [
    "BU_ID",
    "A unique common identifier to allow for cross-referencing with other GDP Center Databases",
  ],
  ["Source 1", "Online weblink or database source to verify the entry"],
  ["Source 2", "Secondary online weblink to double verify the entry"],
];
const zhAttributes = [
  ["CGP ID", "数据库中用于引用每个发电机组的唯一标识符"],
  ["发电机组名称", "发电机组的唯一名称，包含发电技术和装机容量（MW）"],
  ["发电厂名称", "发电厂名称，可能包含多个发电机组"],
  ["装机容量（MW）", "发电机组的总发电装机容量（MW）"],
  ["项目状态", "运营中、建设中或计划中"],
  [
    <>
      投产年份
      <sup>
        <a href="#note1" id="reference1">
          [*]
        </a>
      </sup>
    </>,
    "发电机组进入或计划投产的年份",
  ],
  [
    "投产年份（并购：收购年份）",
    "一般情况下为投产年份，但若该项目为并购投资，且机组早于收购时间已投入运行，则改为使用收购年份。此做法旨在准确反映中国实际开始参与的时间。",
  ],
  ["能源技术", "主要燃料：煤炭、天然气、太阳能、风能等"],
  ["州/省", "发电机组所在州/省"],
  ["国家", "发电机组所在国家"],
  ["地区", "发电机组所在地区"],
  ["年排放量估算", "预计二氧化碳年排放量（以千公吨为单位)"],
  [
    "交易类型",
    "并购、绿地投资、外国直接投资+开发性金融机构融资或仅开发性金融机构融资",
  ],
  ["外国直接投资参与", "是否有中国外国直接投资"],
  ["投资公司", "主要中国投资者"],
  ["投资公司所有权比重%", "主要中国投资者持有的所有权比重，如已知"],
  ["外国直接投资交易类型", "绿地或并购投资"],
  ["是否涉及中国开发性金融机构融资", "是否涉及中国开发性金融机构融资"],
  ["贷款方", "提供贷款的中国开发性金融机构"],
  ["借款方", "贷款的主要接收方"],
  [
    "融资年份",
    "对于外国直接投资（绿地投资和并购），为进行股权投资的年份；对于开发性金融机构，则为贷款承诺年份。",
  ],
  ["BU_ID", "用于波士顿大学全球发展政策研究中心跨数据库引用的编号"],
  ["来源1", "可供验证条目的在线网页链接或数据库来源"],
  ["来源2", "可供对条目进行双重验证的次级在线网页链接"],
];
function enFAQ(props) {
  const { showAbout } = props;
  return (
    <ol>
      <li>
        <h2>Scope and Attributes</h2>
        <ol type="a" className="lettered">
          <li>
            <h3>
              What does this database cover? What does it not cover? What are
              the caveats?
            </h3>
            <p>
              China's overseas economic activity comes in many forms, led by
              diverse entities. The China's Global Power (CGP) Database tracks
              power generating units financed through Chinese foreign direct
              investment (FDI) and/or loans from China's two development finance
              institutions (DFIs)—the Export-Import Bank of China (CHEXIM) and
              the China Development Bank (CDB).
            </p>
            <p>
              The CGP Database does not track lending commitments from Chinese
              commercial banks. This database does not include projects inside
              China.
            </p>
            <p>
              Only projects with more than 10 percent Chinese ownership are
              tracked. The Database does not include investment amounts, as
              these figures are challenging to identify for each project.
            </p>
            <p>
              The Chinese participants indicated for units in the database may
              not be the sole investor or financier of the project. Power
              generating units in the CGP Database may have other sources of
              finance besides Chinese DFI loans and FDI. This could be the case
              for some projects, but oftentimes there are local or other
              international investments and financiers involved as well. When
              using the database for analysis, it is important to recognize that
              a larger project measured by generation capacity does not always
              mean greater monetary amounts of finance from China. The influence
              that Chinese entities have on the projects could be very different
              from project to project due to the different levels of their
              participation.
            </p>
          </li>
          <li>
            <h3>
              What are the specific definitions of all the attributes in the
              database?
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Attribute:</th>
                  <th>Definition:</th>
                </tr>
              </thead>
              <tbody>
                {enAttributes.map((attr, i) => (
                  <tr key={i}>
                    <th score="row">{attr[0]}</th>
                    <td>{attr[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="note">
              <a href="#reference1" id="note1">
                [*]
              </a>
              Year of Commission: Since the status of power plants involved in
              mergers and acquisitions (M&A) deals is usually determined before
              the transaction, commission years would not reflect the activities
              of Chinese capital. For analyses focused on Chinese activity, it
              is more appropriate to use the 'Year of Commission (M&A:
              Acquisition Year)' that captures the start of China's presence.
            </div>
          </li>
          <li>
            <h3>
              What is the relationship between the "Deal Type", "FDI" and "DFI"
              attributes?
            </h3>
            <p>
              There are two types of FDI – greenfield investment, which means
              investment in a project from the ground up, and mergers and
              acquisitions (M&A), which is related to the purchase of shares of
              an existing company.
            </p>
            <p>
              For projects that involve Chinese FDI, but not Chinese DFIs, the
              "FDI" attribute will be "Yes", and the "DFI" attribute will be
              "No". "Deal Type" will reflect which kind of FDI transaction it
              is.
            </p>
            <p>
              For projects that involve both Chinese FDI and Chinese DFIs, the
              "Deal Type" attribute will be "FDI + DFI". Both "FDI" and "DFI"
              will be "Yes".
            </p>
            <p>
              For projects that involve Chinese DFIs but not Chinese FDI, the
              "Deal Type" attribute will be "DFI Only". The "FDI" attribute will
              be "No", and the "DFI" attribute will be "Yes".
            </p>
          </li>
          <li>
            <h3>What does "NA" mean in the database?</h3>
            <p>
              For projects that do not involve FDI, "Investment Company", "FDI
              Deal Type" and "Ownership %" are not applicable (NA); for projects
              that do not involve DFIs, "Lender" and "Borrower" are not
              applicable.
            </p>
            <p>
              For non-fossil fuel units, "Estimated Annual CO<sub>2</sub>{" "}
              Emissions" is NA.
            </p>
            <p>The "State/Province" attribute is NA for Singapore.</p>
          </li>
          <li>
            <h3>
              Which variables are estimated, and which are measured? To what
              extent are they accurate?
            </h3>
            <p>
              Except CO<sub>2</sub> emissions estimates, most power plant
              information is derived from the S&P Global World Electric Power
              Plants Database (WEPP). On a few occasions where confirmed
              projects from official sources are not found in WEPP, they are
              added to the database. Information for the financial attributes
              comes from the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.bu.edu/cgef/#/intro"
              >
                China's Global Energy Finance (CGEF) Database
              </a>{" "}
              and double-verified internet sources (see{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.bu.edu/gdp/2021/03/23/gdp-center-database-methodology-guidebook/"
              >
                Methodology Guidebook
              </a>
              ).
            </p>
            <p>
              For CO<sub>2</sub> emissions estimates, fuel-based capacity
              factors and region-specific emission factors are used to estimate
              emissions from different fuels. More information can be found in
              the CGP Database{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.bu.edu/gdp/2020/10/19/chinas-global-power-database-methodology-note/"
              >
                Methodology Note
              </a>
              . These estimates should be taken as approximations – additional
              information will be needed to conduct a more nuanced calculation.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <h2>Interface Structure</h2>
        <ul>
          <li>
            <h3>
              What do the "Overview" & "Multi-attribute Comparison" tabs do?
            </h3>
            <p>
              The interactive interface provides two different views for the
              data. The Overview provides mapping, bar charts and a
              project-level table for all projects by geographic distribution
              and over time. It also exhibits a breakdown of project capacities
              on both spatial and temporal dimensions by project status,
              technology, deal type and DFI lender for projects with their
              involvement.
            </p>
            <img src={navigation} alt="" />
            <p>
              Spatial aggregation and comparison are available by country and by
              region. View of timeframes is adjustable with the "CHANGE
              TIMEFRAME" button. Single-country or region view is available by
              clicking on the country or region of interest on the map and can
              also be chosen from the dropdown menu on the top left of the map.
              Single-year view is available by clicking on the column of the
              year of interest in the bar chart and can also be chosen from the
              "CHANGE TIMEFRAME" menu.
            </p>
            <img src={controlsAnnotated} alt="" />
            <p>
              Country or region overviews are available under each feature by
              hovering on the maps. Detailed views of country/region projects
              show up when clicked on the country/region or project, and the
              table below will be filtered accordingly. The table can be
              collapsed or expanded by geography and the feature of display. By
              clicking on the column tab of the feature of interest, the user
              can sort the table project by the feature of choice. For more
              project information than what is displayed in the table, the
              floating project information window is available both on the map
              and in the table by hovering and by clicking. The globe icon on
              the top left of the map takes users back to the global view from a
              country/region view.
            </p>
            <p>
              The MULTI-ATTRIBUTE COMPARISON tab allows users to choose multiple
              countries or areas of interest and generate Sankey charts to
              compare the capacity of plants with different status and
              technologies, as well as estimated CO<sub>2</sub> emissions from
              different kinds of power plants in the database between countries.
            </p>
            <p>
              In the MULTI-ATTRIBUTE COMPARISON tab, a line chart will be
              generated to demonstrate estimated annual emissions over time from
              the power plants in the database. A floating window will appear
              while hovering that shows the newly added emissions each year and
              the fossil fuel power plants that lead to these emissions. In the
              line chart, all projects still under planning are assumed to start
              generation in 2030 for simplicity. A corresponding table of the
              projects in the chosen countries/regions will also be generated.
            </p>
          </li>
        </ul>
      </li>
      <li>
        <h2>How to Download the Data</h2>
        <p>
          Fill out the form in the{" "}
          <a href="/#" onClick={showAbout}>
            About/Data Download
          </a>{" "}
          section, and a link for download will be sent to your email from
          gdp@bu.edu. Please check your Spam folder if you do not receive an
          email within a few minutes.
        </p>
      </li>
      <li>
        <h2>Additional Questions?</h2>
        <p>
          For technical inquiries or suggestions regarding this database, please
          contact Diego Morro, <a href="mailto:dm3760@bu.edu">dm3760@bu.edu</a>.
        </p>
        <p>
          For media inquiries, please contact Sam Igo,{" "}
          <a href="mailto:sigo@bu.edu">sigo@bu.edu</a>.
        </p>
      </li>
    </ol>
  );
}

function zhFAQ(props) {
  const { showAbout } = props;
  return (
    <ol>
      <li>
        <h2>范围和特点</h2>
        <ol type="a" className="lettered">
          <li>
            <h3>数据库涵盖了哪些内容？没有涵盖哪些内容？有哪些注意事项？</h3>
            <p>
              中国的海外经济活动形式多样，且由各种不同的实体主导。中国全球电力（CGP）数据库追踪由中国外国直接投资（FDI）和/或由中国进出口银行（CHEXIM）和中国国家开发银行（CDB）这两家中国开发性金融机构（DFIs）参与贷款的海外发电厂。
            </p>
            <p>
              中国全球电力数据库未追踪来自中国商业银行提供的贷款承诺。此数据库也不包括中国境内的项目。
            </p>
            <p>
              该数据库仅追踪中方所有权超过10%的项目。由于难以准确获取单个项目的投资额，因此相关数据未予收录。
            </p>
            <p>
              该数据库中的发电机组标明的中国参与方可能不是项目唯一的投资方或融资方。中国全球电力数据库中的发电机组在中国开发性金融机构融资和中国外国直接投资以外可能还有其他融资渠道。有些项目可能是这种情况，但很多时候还有当地或其他国际投资或融资方的参与。在使用数据库进行分析时还应注意，项目装机容量更大并不总意味着来自中国的融资金额也更高。由于参与程度的不同，中国实体对项目的影响力可能因项目而异。
            </p>
          </li>
          <li>
            <h3>数据库中不同项目属性的具体定义是什么？</h3>
            <table>
              <thead>
                <tr>
                  <th>项目属性:</th>
                  <th>定义:</th>
                </tr>
              </thead>
              <tbody>
                {zhAttributes.map((attr, i) => (
                  <tr key={i}>
                    <th score="row">{attr[0]}</th>
                    <td>{attr[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="note">
              <a href="#reference1" id="note1">
                [*]
              </a>
              投产年份：由于并购交易中涉及的发电厂的状态通常在交易前就已确定，投产年份因而无法反映中国资本的活动。因此，在以中国参与为重点的分析中，采用“投产年份（并购：收购年份）”更为合适。
            </div>
          </li>
          <li>
            <h3>
              “交易类型”、“外国直接投资”和“开发性金融机构”这三个属性之间存在怎样的关系？
            </h3>
            <p>
              外国直接投资（FDI）有两种类型：绿地投资，即从头开始对一个项目的投资；并购（M&A），即涉及购买现有公司的股份。
            </p>
            <p>
              对于涉及中国外国直接投资但不涉及中国开发性金融机构融资的项目，“外国直接投资”一栏为“是”，“开发性金融机构”一栏为“否”。“交易类型”反映该项目属于哪种外国直接投资交易。
            </p>
            <p>
              对于同时涉及中国外国直接投资和中国开发性金融机构融资的项目，“交易类型”一栏为“外国直接投资+开发性金融机构”。“外国直接投资”和“
              开发性金融机构”栏都为“是”。
            </p>
            <p>
              对于涉及中国开发性金融机构融资但不涉及中国外国直接投资的项目，“交易类型”一栏为“仅开发性金融机构”。“外国直接投资”一栏为“否”，“开发性金融机构”一栏为“是”。
            </p>
          </li>
          <li>
            <h3>数据库中的“NA”是什么意思？</h3>
            <p>
              对于不涉及外国直接投资的项目，“投资公司”、“外国直接投资交易类型”以及“所有权比重”栏不适用，显示为NA；对于不涉及开发性金融机构融资的项目，“贷款方”和“借款方”不适用，显示为NA。
            </p>
            <p>
              对于非化石燃料机组，“二氧化碳年排放量估算”栏不适用，显示为NA。
            </p>
            <p>“州/省”栏对新加坡不适用，显示为NA。</p>
          </li>
          <li>
            <h3> 哪些变量是估算的，哪些是测量的？准确度有多高？</h3>
            <p>
              除二氧化碳排放量估算之外，多数发电厂信息来自标普全球（S&P
              Global）的全球发电厂数据库（WEPP）。少数经官方信息源确认但尚未被全球发电厂数据库收录的项目，也被添加进了数据库中。贷款方面的信息来自
              <a
                href="https://www.bu.edu/cgef/?lang=zh#/intro"
                target="_blank"
                rel="noopener noreferrer"
              >
                中国海外能源融资（CGEF）数据库
              </a>
              ，并且经网络来源双重验证（参见
              <a
                href="https://www.bu.edu/gdp/2021/03/23/gdp-center-database-methodology-guidebook/"
                target="_blank"
                rel="noopener noreferrer"
              >
                方法指南
              </a>
              ）。
            </p>
            <p>
              关于二氧化碳排放量估算，估算不同燃料产生的排放量时考虑了与燃料类型相关的装机容量和与地区相关的排放量两个系数。更多信息可见中国全球电力数据库
              <a
                href="https://www.bu.edu/gdp/2020/10/19/chinas-global-power-database-methodology-note/"
                target="_blank"
                rel="noopener noreferrer"
              >
                使用方法注记
              </a>
              。这些估值仅为粗略估算——如要进行更精确的计算，则需更多信息。
            </p>
          </li>
        </ol>
      </li>
      <li>
        <h2>界面结构</h2>
        <ul>
          <li>
            <h3>“概览”和“比较视图”页面有哪些功能？</h3>
            <p>
              数据库的交互式界面提供了两种不同的查看数据的方式。“概览”页面根据所有项目的地理分布和时间范围提供了地图、柱状图以及项目级别表，同时，还根据获得开发性金融机构融资的项目的项目状态、能源类型、交易类型和贷款方展示了项目在地理分布和时间范围维度上装机容量分类。
            </p>
            <img src={navigation_zh} alt="" />
            <p>
              地理分布的汇总和比较可以按国家/地区进行查看。调整时间范围可以点击“修改时间范围”按钮。查看某一国家或地区可以点击地图上感兴趣的国家或地区，也可以从地图左上角的下拉菜单中选择。查看某一年份可以点击柱状图上感兴趣年份的圆柱，也可以从“修改时间范围”菜单中选择。
            </p>
            <img src={controls_zh} alt="" />
            <p>
              在各个视图中，将光标移动至国家或地区，即可显示该国家/地区历年投融资概览。点击国家/地区或项目将显示国家/地区项目的详细信息，下方表单会在筛选之后显示相应信息。表单将根据地理分布和显示专题折叠或显示信息。点击感兴趣专题的圆柱页面，用户可以通过选择专题对表单项目进行分类。如需获取比表单中更多的项目信息，可以通过在地图上悬停光标以及点击表单中相应页面查看项目信息浮窗。地图左上角的地球图标可以将用户从国家/地区视图带回全球视图。{" "}
            </p>
            <p>
              用户在“比较视图”页面可以选择感兴趣的多个国家/地区生成桑基图，比较处于不同投产状态、使用不同能源类型的发电厂的装机容量，比较数据库中国家间不同类型发电厂的二氧化碳年排放量估算。
            </p>
            <p>
              “比较视图”页面还将生成一张折线图，显示数据库中发电厂在一段时间内年排放量估算。在折线图上悬停光标出现的浮动窗口显示每年新增排放量以及产生这些排放的化石燃料发电厂。为简单起见，折线图中所有计划中的项目都假定将于2030年开始发电。页面还将生成选中国家/地区项目的相应列表。{" "}
            </p>
          </li>
        </ul>
      </li>
      <li>
        <h2>如何下载数据</h2>
        <p>
          在
          <a href="/#" onClick={showAbout}>
            "关于/数据下载"
          </a>
          部分填写表格，下载链接将由gdp@bu.edu发送到您的电子邮箱。如果您在几分钟内没有收到邮件，请检查您的垃圾邮件箱。
        </p>
      </li>
      <li>
        <h2>其他问题</h2>
        <p>
          有关数据库的技术咨询或建议，请联系Diego Morro，邮箱:
          <a href="mailto:dm3760@bu.edu">dm3760@bu.edu</a>。
        </p>
        <p>
          有关媒体咨询，请联系Sam Igo，邮箱：
          <a href="mailto:sigo@bu.edu">sigo@bu.edu</a>。
        </p>
      </li>
    </ol>
  );
}

export { enFAQ, zhFAQ };
